import styles from './scroll-container.module.css'
import {clsx} from 'clsx'
import {useEffect, useRef, useState, type RefAttributes, type WheelEvent} from 'react'

export interface ScrollContainerProps {
  children: React.ReactNode
  direction?: 'vertical' | 'horizontal'
  showFading?: boolean
  showBorders?: boolean
  backgroundColor?: string
  attributesForContentDiv?: React.HTMLAttributes<HTMLDivElement>
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>): React.RefCallback<T> {
  return (element: T) => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(element)
      } else if (ref != null) {
        ref.current = element
      }
    }
  }
}

export function ScrollContainer({
  ref,
  children,
  direction = 'vertical',
  showFading = true,
  showBorders = true,
  backgroundColor,
  attributesForContentDiv,
}: ScrollContainerProps & RefAttributes<HTMLDivElement>) {
  const directionClass = styles[direction]
  const [canScrollToStart, setCanScrollToStart] = useState(false)
  const [canScrollToEnd, setCanScrollToEnd] = useState(false)
  const startSentinelRef = useRef<HTMLDivElement>(null)
  const endSentinelRef = useRef<HTMLDivElement>(null)
  const internalRef = useRef<HTMLDivElement>(null)
  const pendingScrollDeltaRef = useRef<number>(0)
  const rafIdRef = useRef<number | null>(null)
  const mergedRef = mergeRefs(ref, internalRef)

  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    const startSentinel = startSentinelRef.current
    const endSentinel = endSentinelRef.current
    const container = internalRef.current

    if (!startSentinel || !endSentinel || !container) return

    // Single IntersectionObserver for both sentinels ensures that when both
    // sentinels' intersection states change simultaneously (e.g. scrolling to
    // the middle), both entries arrive in the same callback and both states
    // update atomically in the same React render.
    observerRef.current = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.target === startSentinel) {
            setCanScrollToStart(!entry.isIntersecting)
          } else if (entry.target === endSentinel) {
            setCanScrollToEnd(!entry.isIntersecting)
          }
        }
      },
      {root: container, threshold: 0, rootMargin: '1px'},
    )

    observerRef.current.observe(startSentinel)
    observerRef.current.observe(endSentinel)

    // In Firefox, the initial IntersectionObserver callback can fire before the
    // container has its final dimensions (e.g. when the workbench first opens
    // with a tab), leading to false positives.
    // ResizeObserver forces the IO to re-evaluate by disconnecting and
    // re-observing both sentinels. Because we use a single IO, both sentinels'
    // initial callbacks arrive in the same invocation, so state updates are
    // atomic and one border can't reset while the other is being set.
    const resizeObserver = new ResizeObserver(() => {
      observerRef.current?.disconnect()
      observerRef.current?.observe(startSentinel)
      observerRef.current?.observe(endSentinel)
    })
    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
      observerRef.current?.disconnect()
    }
  }, [direction])

  // Clean up any pending RAF on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
      pendingScrollDeltaRef.current = 0
    }
  }, [])

  // Convert vertical wheel input to horizontal scroll (only for horizontal containers)
  const onWheel =
    direction === 'horizontal'
      ? (event: WheelEvent<HTMLDivElement>) => {
          if (!event.deltaY) return
          event.preventDefault()

          // Accumulate scroll delta
          pendingScrollDeltaRef.current += event.deltaY

          // Batch updates in a single animation frame
          if (rafIdRef.current === null) {
            rafIdRef.current = requestAnimationFrame(() => {
              const container = internalRef.current
              if (container) {
                container.scrollLeft += pendingScrollDeltaRef.current
              }
              pendingScrollDeltaRef.current = 0
              rafIdRef.current = null
            })
          }
        }
      : undefined

  return (
    <div
      className={clsx(
        styles.scrollContainer,
        directionClass,
        canScrollToStart && styles.canScrollToStart,
        canScrollToEnd && styles.canScrollToEnd,
        showFading && (canScrollToStart || canScrollToEnd) && styles.fadeVisible,
        showBorders && styles.borderVisible,
      )}
      style={
        backgroundColor
          ? ({
              backgroundColor,
              '--scrollContainer-gradient-background': backgroundColor,
            } as React.CSSProperties)
          : undefined
      }
    >
      <div
        ref={mergedRef}
        className={styles.wrapper}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        onWheel={onWheel}
      >
        <div ref={startSentinelRef} className={styles.sentinel} aria-hidden="true" />
        <div {...attributesForContentDiv}>{children}</div>
        <div ref={endSentinelRef} className={styles.sentinel} aria-hidden="true" />
      </div>
    </div>
  )
}
